import { OrderSearchQuery, OrderSearchView, OrderView, OrderEventView } from './order.types';
import type { Timeslot } from '@/shared/lib/timeslot/timeslot.types';
import { externalApiClient } from '@/shared/lib/apiClient';
import axios from 'axios';
import { serializeApiPayload } from '@/shared/lib/typeUtil';

/**
 * 서버 응답의 OrderView 타입
 * 서버에서 받은 원본 데이터 구조
 */
type OrderViewResponse = OrderView;

/**
 * 서버 응답의 OrderSearchView 타입
 */
type OrderSearchViewResponse = OrderSearchView;

/**
 * 서버 응답을 그대로 반환 (타입 변환만 수행)
 * OrderView는 이미 서버 응답과 동일한 구조이므로 변환 불필요
 */
function transformOrderView(response: OrderViewResponse): OrderView {
  return response as OrderView;
}

/**
 * 서버 응답의 OrderSearchView를 그대로 반환
 */
function transformOrderSearchView(response: OrderSearchViewResponse): OrderSearchView {
  return response;
}

const ORDER_SEARCH_MAX_PAGE_SIZE = 100;
const ORDER_SEARCH_PAGE_LOOP_MAX = 200;

function chunkIds<T>(arr: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size) as T[]);
  }
  return out;
}

/** 주문 aggregate UUID 다건 → POST /api/v1/orders/search (field: id, operator: IN) */
function buildOrderAggregateIdsInQuery(
  aggregateIds: readonly string[],
  pageSize: number,
  cursor: string | null | undefined
): OrderSearchQuery {
  return {
    filterGroups: [
      {
        logic: 'AND',
        filters: [
          {
            field: 'id',
            operator: 'IN',
            value: null,
            values: [...aggregateIds],
          },
        ],
      },
    ],
    sortBy: 'orderedAt',
    sortOrder: 'DESC',
    pageSize,
    cursor: cursor === undefined ? null : cursor,
  };
}

/**
 * 한 청크(최대 100개)의 주문 UUID에 대해 id+IN 검색 후, hasNext면 cursor로 전부 수집.
 */
async function searchOrdersByAggregateIdsInChunk(
  aggregateIds: readonly string[]
): Promise<Map<string, OrderView>> {
  const byId = new Map<string, OrderView>();
  if (aggregateIds.length === 0) return byId;

  let cursor: string | null | undefined = undefined;
  for (let i = 0; i < ORDER_SEARCH_PAGE_LOOP_MAX; i++) {
    const page = await searchOrders(
      buildOrderAggregateIdsInQuery(
        aggregateIds,
        Math.min(aggregateIds.length, ORDER_SEARCH_MAX_PAGE_SIZE),
        cursor
      )
    );
    for (const item of page.items) {
      byId.set(item.id, item);
    }
    const haveAll = aggregateIds.every((id) => byId.has(id));
    if (haveAll) break;
    if (!page.hasNext || page.nextCursor == null) break;
    cursor = page.nextCursor;
  }
  return byId;
}

async function searchOrders(query: OrderSearchQuery): Promise<OrderSearchView> {
  try {
    const sanitizedQuery = {
      ...query,
      pageSize: Math.min(Math.max(query.pageSize, 1), ORDER_SEARCH_MAX_PAGE_SIZE),
    };
    const headers = { 'Content-Type': 'application/json' };
    const response = await externalApiClient.post<OrderSearchViewResponse>(
      '/api/v1/orders/search',
      serializeApiPayload(sanitizedQuery),
      { headers }
    );

    return transformOrderSearchView(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '주문 검색에 실패했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

const inFlightGetOrder = new Map<string, Promise<OrderView>>();

async function getOrder(orderId: string): Promise<OrderView> {
  const existing = inFlightGetOrder.get(orderId);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const response = await externalApiClient.get<OrderViewResponse>(`/api/v1/orders/${orderId}`, {
        headers,
      });
      return transformOrderView(response.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '주문 검색에 실패했습니다.';
        throw new Error(errorMessage);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    } finally {
      inFlightGetOrder.delete(orderId);
    }
  })();

  inFlightGetOrder.set(orderId, promise);
  return promise;
}

/**
 * 주문 aggregate UUID 목록으로 주문 조회 — `orders/search`에 `id` + `IN` (100건 초과 시 청크, 페이지는 cursor로 병합).
 * RFQ miso 식별자 다건은 `misoOrderId`+IN 별도 API가 필요하면 추가한다.
 */
async function getOrdersByIds(orderIds: readonly string[]): Promise<OrderView[]> {
  if (orderIds.length === 0) return [];

  const uniqueIds = [...new Set(orderIds)];
  const idChunks = chunkIds(uniqueIds, ORDER_SEARCH_MAX_PAGE_SIZE);

  const chunkMaps = await Promise.all(
    idChunks.map((ids) => searchOrdersByAggregateIdsInChunk(ids))
  );

  const byId = new Map<string, OrderView>();
  for (const m of chunkMaps) {
    for (const [id, order] of m) {
      byId.set(id, order);
    }
  }

  return orderIds.map((id) => {
    const order = byId.get(id);
    if (!order) {
      throw new Error(`주문을 찾을 수 없습니다: ${id}`);
    }
    return order;
  });
}

/**
 * 방문 상담 일정 예약 요청 타입
 */
export interface AppointVisitCounselScheduleRequest {
  plannerId: string;
  managerId: string;
  visitAt: string; // Timeslot (ISO-8601_ts 형식)
  memo?: string;
}

/**
 * 방문 상담 일정 변경 요청 타입
 */
export interface ChangeVisitCounselScheduleRequest {
  plannerId?: string;
  managerId?: string;
  visitAt: string; // Timeslot (ISO-8601_ts 형식)
  memo?: string;
}

/**
 * 주문 취소 요청 타입 (API enum 값 사용)
 */
export interface CancelOrderRequest {
  reason: string;
  memo: string;
}

/**
 * 방문일정 조율 요청 타입
 */
export interface InitiateSchedulingRequest {
  coordinationAvailability: { type: 'Anytime' } | { type: 'AfterDays'; days: number };
  preferredVisitSchedules: Timeslot[];
  memo: string;
}

/**
 * 이사 정보 수정 요청 타입
 */
export interface ChangeMovingDetailRequest {
  requesterId: string;
  movingDetail: {
    moveDate: string;
    departure: { location: string; supplyArea: number };
    arrival: { location: string; supplyArea: number };
  };
  memo: string;
}

/**
 * 중복됨 처리 요청 타입
 */
export interface MarkUnqualifiedRequest {
  reason: string;
  activeOrderId?: string;
  memo: string;
}

/**
 * 재견적 처리 요청 타입
 */
export interface MarkRequoteRequest {
  reason: string;
  activeOrderId: string;
  memo: string;
}

/**
 * 방문 상담 일정 예약
 * @param orderId 주문 ID
 * @param request 예약 요청 데이터
 * @returns 예약된 주문 정보
 */
async function appointVisitSchedule(
  orderId: string,
  request: AppointVisitCounselScheduleRequest
): Promise<OrderView> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const response = await externalApiClient.post<OrderViewResponse>(
      `/api/v1/orders/${orderId}/visit-counsel/appoint`,
      serializeApiPayload(request),
      { headers }
    );

    return transformOrderView(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '방문 상담 일정 예약에 실패했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

/** API가 기대하는 change 요청 형식 (counselor, schedule, memo) */
interface ChangeVisitCounselApiRequest {
  counselor?: string;
  schedule: string;
  memo?: string;
}

function toChangeApiRequest(
  request: ChangeVisitCounselScheduleRequest
): ChangeVisitCounselApiRequest {
  const api: ChangeVisitCounselApiRequest = {
    schedule: request.visitAt,
    memo: request.memo,
  };
  if (request.plannerId) api.counselor = request.plannerId;
  return api;
}

/**
 * 방문 상담 일정 변경
 * @param orderId 주문 ID
 * @param request 변경 요청 데이터
 * @returns 변경된 주문 정보
 */
async function changeVisitSchedule(
  orderId: string,
  request: ChangeVisitCounselScheduleRequest
): Promise<OrderView> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const apiRequest = toChangeApiRequest(request);
    const response = await externalApiClient.put<OrderViewResponse>(
      `/api/v1/orders/${orderId}/visit-counsel/change`,
      serializeApiPayload(apiRequest),
      { headers }
    );

    return transformOrderView(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '방문 상담 일정 변경에 실패했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

/**
 * 주문 이벤트 조회 (V1)
 * @param orderId 주문 ID
 * @returns 주문 이벤트 뷰
 */
async function getOrderEvents(orderId: string): Promise<OrderEventView> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const response = await externalApiClient.get<OrderEventView>(
      `/api/v1/orders/${orderId}/events`,
      { headers }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '주문 이벤트 조회에 실패했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

/**
 * 주문 이벤트 조회 (V2 - checkpoint 포함)
 * @param orderId 주문 ID
 * @returns 주문 이벤트 뷰 (events: { event, checkpoint }[])
 */
async function getOrderEventsV2(orderId: string): Promise<OrderEventView> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const response = await externalApiClient.get<OrderEventView>(
      `/api/v2/orders/${orderId}/events`,
      { headers }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '주문 이벤트 조회에 실패했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

/**
 * 방문일정 조율 시작
 * @param orderId 주문 ID
 * @param request 조율 요청
 * @returns 주문 정보
 */
async function initiateScheduling(
  orderId: string,
  request: InitiateSchedulingRequest
): Promise<OrderView> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const apiRequest = {
      coordinationAvailability:
        request.coordinationAvailability.type === 'Anytime'
          ? { type: 'Anytime' }
          : { type: 'AfterDays', days: request.coordinationAvailability.days },
      preferredVisitSchedules: request.preferredVisitSchedules,
      memo: request.memo,
    };
    const response = await externalApiClient.post<OrderViewResponse>(
      `/api/v1/orders/${orderId}/schedule-coordination`,
      apiRequest,
      { headers }
    );
    return transformOrderView(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '방문일정 조율 시작에 실패했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

/**
 * 이사 정보 수정
 * @param orderId 주문 ID
 * @param request 수정 요청
 * @returns 주문 정보
 */
async function changeMovingDetail(
  orderId: string,
  request: ChangeMovingDetailRequest
): Promise<OrderView> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const response = await externalApiClient.put<OrderViewResponse>(
      `/api/v1/orders/${orderId}/moving-detail`,
      request,
      { headers }
    );
    return transformOrderView(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '이사 정보 수정에 실패했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

/**
 * 중복됨 처리
 * @param orderId 주문 ID
 * @param request 처리 요청
 * @returns 주문 정보
 */
async function markUnqualified(
  orderId: string,
  request: MarkUnqualifiedRequest
): Promise<OrderView> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const response = await externalApiClient.post<OrderViewResponse>(
      `/api/v1/orders/${orderId}/unqualified`,
      serializeApiPayload(request),
      { headers }
    );
    return transformOrderView(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '중복됨 처리에 실패했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

/**
 * 재견적 처리
 * @param orderId 주문 ID
 * @param request 처리 요청 (reason, activeOrderId 필수)
 * @returns 주문 정보
 */
async function markRequote(orderId: string, request: MarkRequoteRequest): Promise<OrderView> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const response = await externalApiClient.post<OrderViewResponse>(
      `/api/v1/orders/${orderId}/requote`,
      request,
      { headers }
    );
    return transformOrderView(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '재견적 처리에 실패했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

/**
 * 주문 취소
 * @param orderId 주문 ID
 * @param request 취소 요청 (reason: API enum, memo: 1~500자)
 * @returns 취소된 주문 정보
 */
async function cancelOrder(orderId: string, request: CancelOrderRequest): Promise<OrderView> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const response = await externalApiClient.post<OrderViewResponse>(
      `/api/v1/orders/${orderId}/cancel`,
      request,
      { headers }
    );
    return transformOrderView(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '주문 취소에 실패했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

export const orderApi = {
  searchOrders,
  getOrder,
  getOrdersByIds,
  appointVisitSchedule,
  changeVisitSchedule,
  cancelOrder,
  initiateScheduling,
  changeMovingDetail,
  markUnqualified,
  markRequote,
  getOrderEvents,
  getOrderEventsV2,
};
